import { useMemo } from 'react';

import { calculateMetricsForWarehouses } from '../../utils/calculate-metrics-for-warehouses';

/**
 * @typedef {object} MetricComparison
 * @property {number | null} before - The baseline metric value
 * @property {number | null} after - The new metric value
 * @property {number | null} improvement - The improvement percentage
 */

/**
 * @typedef {object} ZoneComparisonMetrics
 * @property {MetricComparison} zone - Zone comparison metrics
 * @property {MetricComparison} distance - Distance comparison metrics
 * @property {MetricComparison} fulfillmentTime - Fulfillment time comparison metrics
 */

/**
 * Calculate improvement percentage for a metric
 * @param {number | null} before - The baseline value
 * @param {number | null} after - The new value
 * @returns {number | null} The improvement percentage, or null if calculation is not possible
 */
const calculateImprovement = (before, after) => {
  if (after === null || before === null || before === 0) {
    return null;
  }
  return ((before - after) / before) * 100;
};

/**
 * @param {Map<string, number>} baselineStateZoneMap
 * @param {Map<string, number>} selectedStateZoneMap
 * @returns {ZoneComparisonMetrics}
 */
export const useZoneComparisonMetrics = (baselineStateZoneMap, selectedStateZoneMap) => {
  const beforeMetrics = useMemo(() => calculateMetricsForWarehouses(baselineStateZoneMap), [baselineStateZoneMap]);

  return useMemo(() => {
    const afterMetrics = calculateMetricsForWarehouses(selectedStateZoneMap);

    return {
      zone: {
        before: beforeMetrics.zone,
        after: afterMetrics.zone,
        improvement: calculateImprovement(beforeMetrics.zone, afterMetrics.zone),
      },
      distance: {
        before: beforeMetrics.distance,
        after: afterMetrics.distance,
        improvement: calculateImprovement(beforeMetrics.distance, afterMetrics.distance),
      },
      fulfillmentTime: {
        before: beforeMetrics.fulfillmentTime,
        after: afterMetrics.fulfillmentTime,
        improvement: calculateImprovement(beforeMetrics.fulfillmentTime, afterMetrics.fulfillmentTime),
      },
    };
  }, [beforeMetrics, selectedStateZoneMap]);
};
